import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Delete,
  Schedule,
  Visibility,
} from '@mui/icons-material';

// ============================================================================
// Types
// ============================================================================

export type AlertActionType = 'acknowledge' | 'dismiss' | 'delete' | 'review';

export interface AlertActionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (suppressMinutes?: number) => Promise<void>;
  action: AlertActionType;
  alertName: string;
  loading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const BORDER_RADIUS = '10px';

const suppressPresets = [
  { label: 'None', value: 0 },
  { label: '1 hr', value: 60 },
  { label: '4 hrs', value: 240 },
  { label: '24 hrs', value: 1440 },
  { label: '1 week', value: 10080 },
  { label: 'Custom', value: -1 },
];

const actionConfig: Record<AlertActionType, {
  title: string;
  description: string;
  confirmText: string;
  color: 'warning' | 'success' | 'error' | 'info';
  icon: React.ReactNode;
  showSuppress?: boolean;
}> = {
  acknowledge: {
    title: 'Acknowledge Alert',
    description: 'Acknowledging this alert marks it as seen. The webhook will not revert it to "new" while acknowledged.',
    confirmText: 'Acknowledge',
    color: 'warning',
    icon: <CheckCircle color="warning" />,
  },
  dismiss: {
    title: 'Dismiss Alert',
    description: 'Dismissing resolves this alert. Choose a suppress duration to prevent it from re-opening if the alert fires again within that window.',
    confirmText: 'Dismiss',
    color: 'success',
    icon: <Close color="success" />,
  },
  delete: {
    title: 'Delete Alert',
    description: 'Deleting hides this alert and prevents the webhook from re-creating it. Choose a suppress duration, or leave it as "None" to suppress indefinitely.',
    confirmText: 'Delete',
    color: 'error',
    icon: <Delete color="error" />,
  },
  review: {
    title: 'Mark Alert as Reviewed',
    description: 'This alert was automatically resolved without being acknowledged. Marking it as reviewed confirms you have seen it and moves it to resolved.',
    confirmText: 'Mark Reviewed',
    color: 'info',
    icon: <Visibility color="info" />,
    showSuppress: false,
  },
};

// ============================================================================
// Component
// ============================================================================

export function AlertActionDialog({
  open,
  onClose,
  onConfirm,
  action,
  alertName,
  loading = false,
}: AlertActionDialogProps) {
  const [selectedPreset, setSelectedPreset] = useState<number>(0);
  const [customHours, setCustomHours] = useState<string>('');
  const [customMinutes, setCustomMinutes] = useState<string>('');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedPreset(0);
      setCustomHours('');
      setCustomMinutes('');
    }
  }, [open]);

  const showCustomFields = selectedPreset === -1;

  const getSuppressMinutes = (): number | undefined => {
    if (selectedPreset === 0) return undefined;
    if (selectedPreset === -1) {
      const hours = parseInt(customHours, 10) || 0;
      const minutes = parseInt(customMinutes, 10) || 0;
      const total = hours * 60 + minutes;
      return total > 0 ? total : undefined;
    }
    return selectedPreset;
  };

  const handleConfirm = async () => {
    await onConfirm(getSuppressMinutes());
  };

  const isCustomValid = selectedPreset !== -1 || (parseInt(customHours, 10) > 0 || parseInt(customMinutes, 10) > 0);
  const config = actionConfig[action];

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: BORDER_RADIUS },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {config.icon}
        {config.title}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {config.description}
          </Typography>

          <Box
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" fontWeight={500}>
              {alertName}
            </Typography>
          </Box>

          {/* Suppress Duration — hidden for review action */}
          {config.showSuppress !== false && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule fontSize="small" />
                Suppress for:
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: showCustomFields ? 2 : 0 }}>
                {suppressPresets.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={selectedPreset === preset.value ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setSelectedPreset(preset.value)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: BORDER_RADIUS,
                      minWidth: 72,
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Box>

              {/* Custom Duration Fields */}
              <Collapse in={showCustomFields}>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    size="small"
                    label="Hours"
                    type="number"
                    value={customHours}
                    onChange={(e) => setCustomHours(e.target.value)}
                    inputProps={{ min: 0, max: 168 }}
                    sx={{
                      width: 100,
                      '& .MuiOutlinedInput-root': { borderRadius: BORDER_RADIUS },
                    }}
                  />
                  <TextField
                    size="small"
                    label="Minutes"
                    type="number"
                    value={customMinutes}
                    onChange={(e) => setCustomMinutes(e.target.value)}
                    inputProps={{ min: 0, max: 59 }}
                    sx={{
                      width: 100,
                      '& .MuiOutlinedInput-root': { borderRadius: BORDER_RADIUS },
                    }}
                  />
                </Box>
              </Collapse>
            </>
          )}

          {action === 'delete' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              The alert will be hidden from all views and the webhook will not re-create it.
              {selectedPreset === 0 && ' It will remain suppressed indefinitely until manually restored.'}
              {selectedPreset > 0 && ` After the suppress window expires, the alert will be automatically restored.`}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color={config.color}
          onClick={handleConfirm}
          disabled={loading || !isCustomValid}
          startIcon={loading ? <CircularProgress size={16} /> : config.icon}
          sx={{ textTransform: 'none' }}
        >
          {loading ? 'Processing...' : config.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AlertActionDialog;
